import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport, WheelType } from '@sorare/core/src/__generated__/globalTypes';
import { SilkBackground } from '@sorare/core/src/atoms/animations/SilkBackground';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM, LabelL } from '@sorare/core/src/atoms/typography';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { Slot } from '../Slot';
import type {
  WheelOdds,
  WheelOddsVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(SilkBackground)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    width: 400px;
  }
  @media ${desktopAndAbove} {
    width: 800px;
  }
  min-height: 100%;
  text-align: center;
  background-color: var(--c-nd-50-opaque);
  padding: var(--double-unit);
`;
const query = gql`
  query WheelOdds($sport: Sport!, $wheelType: WheelType!) {
    market {
      id
      wheelOdds(sport: $sport, wheelType: $wheelType) {
        id
        slots {
          probableConfigs {
            id
            ...Slot_probabilisticBundleSlotConfig
          }
        }
      }
    }
  }
  ${Slot.fragments.ProbabilisticBundleSlotConfig}
` as TypedDocumentNode<WheelOdds, WheelOddsVariables>;

type Props = {
  sport?: Sport;
  onClose?: () => void;
  golden?: boolean;
};

export const Odds = ({ sport = Sport.FOOTBALL, onClose, golden }: Props) => {
  const [wheelType, setWheelType] = useState<WheelType>(
    golden ? WheelType.GOLDEN : WheelType.STANDARD
  );
  const { data } = useQuery(query, {
    variables: {
      sport,
      wheelType,
    },
  });

  const probableConfigs = [
    ...(data?.market?.wheelOdds?.slots[0].probableConfigs ?? []),
  ];

  probableConfigs?.sort((a, b) => a.probability - b.probability);

  return (
    <Wrapper
      color={wheelType === WheelType.GOLDEN ? '#ffc55a' : '#2b00ab'}
      fallback="div"
    >
      {!!onClose && <CloseButton size="small" onClose={onClose} />}
      <LabelL bold>
        <FormattedMessage id="wheel.odds.title" defaultMessage="Rewards" />
      </LabelL>
      <BodyM>
        <FormattedMessage
          id="wheel.odds.description"
          defaultMessage="Spin to get a chance to win one of the following rewards"
        />
      </BodyM>
      <TabBar value={wheelType} variant="secondary" fullWidth forceMobileLayout>
        <Tab
          value={WheelType.STANDARD}
          onClick={() => {
            setWheelType(WheelType.STANDARD);
          }}
        >
          <FormattedMessage
            id="wheel.odds.tab.regular"
            defaultMessage="Regular"
          />
        </Tab>
        <Tab
          value={WheelType.GOLDEN}
          onClick={() => {
            setWheelType(WheelType.GOLDEN);
          }}
        >
          <FormattedMessage
            id="wheel.odds.tab.golden"
            defaultMessage="Golden"
          />
        </Tab>
      </TabBar>
      <Grid>
        {probableConfigs.map((probableConfig, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Slot key={index} config={probableConfig} />
        ))}
      </Grid>
    </Wrapper>
  );
};
